/**
 * Admin - Plans Management
 * Gestão de planos e preços
 */
import { useState, useEffect } from 'react'
import {
  CreditCard,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  RefreshCw,
  Star,
  Building2,
  Users,
  Shield,
  Brain,
  HardHat,
  ScanFace,
  Activity,
  Save,
} from 'lucide-react'
import { adminDb } from '../lib/supabase'

interface Plan {
  id: string
  name: string
  slug: string
  description: string
  price: number
  billing_period: 'monthly' | 'yearly'
  features: string[]
  limits: {
    employees: number
    locations: number
    storage_gb: number
    api_calls: number
  }
  is_popular: boolean
  is_active: boolean
  created_at: string
}

const defaultFeatures = {
  starter: [
    'Ponto Facial',
    'Dashboard básico',
    'Até 50 colaboradores',
    '1 localidade',
    'Suporte por email',
    'Relatórios básicos',
  ],
  business: [
    'Tudo do Starter',
    'Análise de Produtividade',
    'Verificação de EPIs',
    'Até 200 colaboradores',
    '5 localidades',
    'Suporte prioritário',
    'API básica',
    'Relatórios avançados',
  ],
  enterprise: [
    'Tudo do Business',
    'IA Comportamental',
    'Colaboradores ilimitados',
    'Localidades ilimitadas',
    'Suporte 24/7',
    'API completa',
    'SSO / SAML',
    'Integrações customizadas',
    'SLA garantido',
    'Gerente de conta dedicado',
  ],
}

export default function Plans() {
  const [loading, setLoading] = useState(true)
  const [plans, setPlans] = useState<Plan[]>([])
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)

  useEffect(() => {
    loadPlans()
  }, [])

  const loadPlans = async () => {
    try {
      setLoading(true)
      const { data, error } = await adminDb.plans.getAll()

      if (data && data.length > 0) {
        setPlans(data)
      } else {
        // Demo data
        setPlans([
          {
            id: '1',
            name: 'Starter',
            slug: 'starter',
            description: 'Ideal para pequenas equipes começando com monitoramento',
            price: 299,
            billing_period: 'monthly',
            features: defaultFeatures.starter,
            limits: { employees: 50, locations: 1, storage_gb: 10, api_calls: 1000 },
            is_popular: false,
            is_active: true,
            created_at: '2024-01-01',
          },
          {
            id: '2',
            name: 'Business',
            slug: 'business',
            description: 'Para empresas em crescimento que precisam de mais recursos',
            price: 799,
            billing_period: 'monthly',
            features: defaultFeatures.business,
            limits: { employees: 200, locations: 5, storage_gb: 50, api_calls: 10000 },
            is_popular: true,
            is_active: true,
            created_at: '2024-01-01',
          },
          {
            id: '3',
            name: 'Enterprise',
            slug: 'enterprise',
            description: 'Solução completa para grandes organizações',
            price: 1999,
            billing_period: 'monthly',
            features: defaultFeatures.enterprise,
            limits: { employees: -1, locations: -1, storage_gb: 500, api_calls: -1 },
            is_popular: false,
            is_active: true,
            created_at: '2024-01-01',
          },
        ])
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSavePlan = async (plan: Plan) => {
    try {
      if (plan.id) {
        await adminDb.plans.update(plan.id, plan)
      } else {
        await adminDb.plans.create(plan)
      }
      await loadPlans()
      setEditingPlan(null)
    } catch (error) {
      console.error('Erro ao salvar plano:', error)
    }
  }

  const handleDeletePlan = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este plano?')) return

    try {
      await adminDb.plans.delete(id)
      setPlans(plans.filter(p => p.id !== id))
    } catch (error) {
      console.error('Erro ao excluir plano:', error)
    }
  }

  const getFeatureIcon = (feature: string) => {
    if (feature.includes('Ponto')) return <ScanFace className="w-4 h-4" />
    if (feature.includes('Produtividade')) return <Activity className="w-4 h-4" />
    if (feature.includes('EPI')) return <HardHat className="w-4 h-4" />
    if (feature.includes('IA')) return <Brain className="w-4 h-4" />
    if (feature.includes('Suporte')) return <Shield className="w-4 h-4" />
    if (feature.includes('colaboradores')) return <Users className="w-4 h-4" />
    if (feature.includes('localidade')) return <Building2 className="w-4 h-4" />
    return <Check className="w-4 h-4" />
  }

  const getPlanColor = (slug: string) => {
    switch (slug) {
      case 'starter': return 'from-slate-500 to-slate-600'
      case 'business': return 'from-violet-500 to-purple-600'
      case 'enterprise': return 'from-amber-500 to-orange-600'
      default: return 'from-slate-500 to-slate-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-violet-400" />
            Planos
          </h1>
          <p className="text-slate-400">Gerencie os planos e preços da plataforma</p>
        </div>
        <button
          className="px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-white transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Plano
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative admin-card overflow-hidden ${
              plan.is_popular ? 'ring-2 ring-violet-500' : ''
            }`}
          >
            {/* Popular Badge */}
            {plan.is_popular && (
              <div className="absolute top-0 right-0">
                <div className="bg-violet-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Popular
                </div>
              </div>
            )}

            {/* Header */}
            <div className={`p-4 rounded-xl bg-gradient-to-r ${getPlanColor(plan.slug)} mb-4`}>
              <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              <p className="text-white/80 text-sm mt-1">{plan.description}</p>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-white">R$ {plan.price.toLocaleString('pt-BR')}</span>
                <span className="text-slate-400">/mês</span>
              </div>
              {plan.billing_period === 'yearly' && (
                <p className="text-xs text-emerald-400 mt-1">Economia de 20% no plano anual</p>
              )}
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <p className="text-lg font-bold text-white">
                  {plan.limits.employees === -1 ? '∞' : plan.limits.employees}
                </p>
                <p className="text-xs text-slate-500">Colaboradores</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <p className="text-lg font-bold text-white">
                  {plan.limits.locations === -1 ? '∞' : plan.limits.locations}
                </p>
                <p className="text-xs text-slate-500">Localidades</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <p className="text-lg font-bold text-white">{plan.limits.storage_gb}GB</p>
                <p className="text-xs text-slate-500">Storage</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <p className="text-lg font-bold text-white">
                  {plan.limits.api_calls === -1 ? '∞' : `${(plan.limits.api_calls / 1000).toFixed(0)}k`}
                </p>
                <p className="text-xs text-slate-500">API/mês</p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 mb-6">
              {plan.features.slice(0, 6).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="text-emerald-400">
                    {getFeatureIcon(feature)}
                  </div>
                  <span className="text-slate-300">{feature}</span>
                </div>
              ))}
              {plan.features.length > 6 && (
                <p className="text-xs text-slate-500 pl-6">
                  +{plan.features.length - 6} mais recursos
                </p>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-700/50">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                plan.is_active
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-slate-500/10 text-slate-400'
              }`}>
                {plan.is_active ? 'Ativo' : 'Inativo'}
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingPlan(plan)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Comparison Table */}
      <div className="admin-card">
        <h2 className="text-lg font-semibold text-white mb-6">Comparativo de Planos</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Recurso</th>
                {plans.map(plan => (
                  <th key={plan.id} className="text-center px-4 py-3 text-sm font-medium text-white">
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">Ponto Facial</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">Análise de Produtividade</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    {plan.slug === 'starter' ? (
                      <X className="w-5 h-5 text-slate-600 mx-auto" />
                    ) : (
                      <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">Verificação de EPIs</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    {plan.slug === 'starter' ? (
                      <X className="w-5 h-5 text-slate-600 mx-auto" />
                    ) : (
                      <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">IA Comportamental</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    {plan.slug === 'enterprise' ? (
                      <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-slate-600 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">API Access</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    {plan.slug === 'starter' ? (
                      <X className="w-5 h-5 text-slate-600 mx-auto" />
                    ) : plan.slug === 'business' ? (
                      <span className="text-xs text-amber-400">Básica</span>
                    ) : (
                      <span className="text-xs text-emerald-400">Completa</span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">Suporte</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3 text-xs">
                    {plan.slug === 'starter' ? (
                      <span className="text-slate-400">Email</span>
                    ) : plan.slug === 'business' ? (
                      <span className="text-amber-400">Prioritário</span>
                    ) : (
                      <span className="text-emerald-400">24/7 Dedicado</span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-slate-300">SSO / SAML</td>
                {plans.map(plan => (
                  <td key={plan.id} className="text-center px-4 py-3">
                    {plan.slug === 'enterprise' ? (
                      <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-slate-600 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingPlan && (
        <PlanEditModal
          plan={editingPlan}
          onSave={handleSavePlan}
          onClose={() => setEditingPlan(null)}
        />
      )}
    </div>
  )
}

// Plan Edit Modal Component
function PlanEditModal({
  plan,
  onSave,
  onClose,
}: {
  plan: Plan
  onSave: (plan: Plan) => void
  onClose: () => void
}) {
  const [formData, setFormData] = useState(plan)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Editar Plano</h2>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Preço (R$)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Colaboradores</label>
              <input
                type="number"
                value={formData.limits.employees}
                onChange={(e) => setFormData({
                  ...formData,
                  limits: { ...formData.limits, employees: Number(e.target.value) }
                })}
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_popular}
                onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                className="w-4 h-4 rounded border-slate-600 text-violet-500 focus:ring-violet-500/50"
              />
              <span className="text-sm text-slate-300">Marcar como Popular</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-slate-600 text-violet-500 focus:ring-violet-500/50"
              />
              <span className="text-sm text-slate-300">Ativo</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-white transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
